import React, {
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cloneDeep, isString } from 'lodash';
import { momentInstance } from 'helpers/date-time';

import { cn } from 'helpers/css';
import { Button } from 'shadcn/components/button';
import { Calendar } from 'shadcn/components/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/popover';
import { CalendarIcon, Check, X, XCircle } from 'lucide-react';
import Select24Hours from './Select24Hours';
import NewDesign from '../NewDesign';
import { Separator } from 'shadcn/components/separator';
import Select24HoursAndMinutes from './Select24HoursAndMinutes';

const propTypes = {};

const DateOptionEnum = {
  today: 'today',
  yesterday: 'yesterday',
  last7Days: 'last7Days',
  last30Days: 'last30Days',
  thisMonth: 'thisMonth',
  lastMonth: 'lastMonth',
  thirtyMin: 'thirtyMin',
  oneHour: 'oneHour',
  thisWeek: 'thisWeek',
  lastWeek: 'lastWeek',
  last1Days: 'last1Days',
  thisYear: 'thisYear',
};

const customDateOption = {
  label: 'Custom range',
  value: 'customRange',
};

const optionsDate = [
  // {
  //   label: '30 Min',
  //   value: DateOptionEnum.thirtyMin,
  // },
  // {
  //   label: '1 Hour',
  //   value: DateOptionEnum.oneHour,
  // },
  {
    label: 'Today',
    value: DateOptionEnum.today,
  },
  {
    label: 'Yesterday',
    value: DateOptionEnum.yesterday,
  },
  {
    label: 'This week',
    value: DateOptionEnum.thisWeek,
  },
  {
    label: 'Last week',
    value: DateOptionEnum.lastWeek,
  },
  {
    label: 'Last 7 Days',
    value: DateOptionEnum.last7Days,
  },
  {
    label: 'Last 30 Days',
    value: DateOptionEnum.last30Days,
  },
  {
    label: 'This month',
    value: DateOptionEnum.thisMonth,
  },
  {
    label: 'Last month',
    value: DateOptionEnum.lastMonth,
  },
  {
    label: 'This year',
    value: DateOptionEnum.thisYear,
  },
];

const parseInitialValue = (date) => {
  if (!!date) {
    if (isString(date)) {
      return new Date(date);
    }

    return date;
  }

  return undefined;
};

const DateRange = (props) => {
  const {
    value,
    onSubmit,
    label = '',
    isClearable,
    classNameContainer,
    innerRef,
    msgError,
    onClickClear,
    disableSelectTime,
    placeholder,
    onlyHours,
    v2,
    required,
  } = props;
  //! State
  const [open, setOpen] = useState(false);
  const [quickSelectDate, setQuickSelectDate] = useState(customDateOption);
  const [dateRanges, setDateRanges] = useState(value);
  const [month, setMonth] = useState(momentInstance().subtract(1, 'month').toDate());

  const [initialDateRanges] = useState(value);
  const lastTimeRef = useRef({ from: null, to: null });

  const getTimePayload = useCallback((momentObj) => {
    if (!momentObj) return null;
    return {
      hours: momentObj.hours(),
      minutes: momentObj.minutes(),
      seconds: momentObj.seconds(),
    };
  }, []);

  const applyTimeToMoment = useCallback((momentObj, timePayload) => {
    if (!momentObj || !timePayload) return;
    momentObj.set({
      hour: timePayload.hours ?? 0,
      minute: timePayload.minutes ?? 0,
      second: timePayload.seconds ?? 0,
    });
  }, []);

  useEffect(() => {
    setDateRanges(value);

    //* In case user click reset -> initialDateRanges === nextDateRanges -> reset the current month
    if (JSON.stringify(initialDateRanges) === JSON.stringify(value)) {
      setMonth(momentInstance().subtract(1, 'month').toDate());
    }
  }, [value, initialDateRanges]);

  useEffect(() => {
    if (dateRanges?.from) {
      const momentFrom = momentInstance(dateRanges.from);
      lastTimeRef.current.from = getTimePayload(momentFrom);
    }
    if (dateRanges?.to) {
      const momentTo = momentInstance(dateRanges.to);
      lastTimeRef.current.to = getTimePayload(momentTo);
    }
  }, [dateRanges?.from, dateRanges?.to, getTimePayload]);

  const format = useMemo(() => {
    if (disableSelectTime) {
      return 'DD/MM/YYYY';
    }

    // if (onlyHours) {
    //   return 'DD/MM/YYYY HH:mm';
    // }

    return 'DD/MM/YYYY HH:mm';
  }, [disableSelectTime, onlyHours]);

  const from = parseInitialValue(dateRanges?.from);
  const to = parseInitialValue(dateRanges?.to);

  const isInvalid = !!msgError;

  //! Effect
  const findDateEnums = useCallback((field) => {
    return optionsDate.find((el) => el.value === field) || customDateOption;
  }, []);

  useImperativeHandle(
    innerRef,
    () => {
      return {
        resetQuickSelectionByDefault: () => {
          setQuickSelectDate(customDateOption);
        },
      };
    },
    []
  );

  useEffect(() => {
    if (from && to) {
      const fromMoment = momentInstance(from);
      const toMoment = momentInstance(to);

      //* 30 Mins
      if (toMoment.diff(fromMoment, 'minutes') === 30) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.thirtyMin));
        return;
      }

      //* 1 Hour
      if (toMoment.diff(fromMoment, 'hours') === 1) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.oneHour));
        return;
      }

      //* Today
      if (
        fromMoment.toDate().getTime() === momentInstance().startOf('date').toDate().getTime() &&
        toMoment.toDate().getTime() === momentInstance().endOf('date').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.today));
        return;
      }

      //* Yesterday
      if (
        fromMoment.toDate().getTime() ===
          momentInstance().subtract(1, 'days').startOf('date').toDate().getTime() &&
        toMoment.toDate().getTime() ===
          momentInstance().subtract(1, 'days').endOf('date').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.yesterday));
        return;
      }

      //* This week
      if (
        fromMoment.toDate().getTime() === momentInstance().startOf('weeks').toDate().getTime() &&
        toMoment.toDate().getTime() === momentInstance().endOf('weeks').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.thisWeek));
        return;
      }

      //* Last week
      if (
        fromMoment.toDate().getTime() ===
          momentInstance().subtract(1, 'weeks').startOf('weeks').toDate().getTime() &&
        toMoment.toDate().getTime() ===
          momentInstance().subtract(1, 'weeks').endOf('weeks').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.lastWeek));
        return;
      }

      //* Last 7 Days
      if (
        fromMoment.toDate().getTime() ===
          momentInstance().subtract(7, 'days').startOf('date').toDate().getTime() &&
        toMoment.toDate().getTime() === momentInstance().endOf('date').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.last7Days));
        return;
      }

      //* Last 30 Days
      if (
        fromMoment.toDate().getTime() ===
          momentInstance().subtract(30, 'days').startOf('date').toDate().getTime() &&
        toMoment.toDate().getTime() === momentInstance().endOf('date').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.last30Days));
        return;
      }

      //* This month
      if (
        fromMoment.toDate().getTime() === momentInstance().startOf('months').toDate().getTime() &&
        toMoment.toDate().getTime() === momentInstance().endOf('months').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.thisMonth));
        return;
      }

      //* Last month
      if (
        fromMoment.toDate().getTime() ===
          momentInstance().subtract(1, 'months').startOf('months').toDate().getTime() &&
        toMoment.toDate().getTime() ===
          momentInstance().subtract(1, 'months').endOf('months').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.lastMonth));
        return;
      }

      //* This year
      if (
        fromMoment.toDate().getTime() === momentInstance().startOf('years').toDate().getTime() &&
        toMoment.toDate().getTime() === momentInstance().endOf('years').toDate().getTime()
      ) {
        setQuickSelectDate(findDateEnums(DateOptionEnum.thisYear));
        return;
      }

      setQuickSelectDate(customDateOption);
    } else {
      setQuickSelectDate(customDateOption);
    }
  }, [from, to]);

  useEffect(() => {
    if (!open) {
      setDateRanges(value);
    }
  }, [open]);

  //! Function
  const switchDateByQuickDate = useCallback(
    (dateType) => {
      //* 30 min
      if (dateType === DateOptionEnum.thirtyMin) {
        setDateRanges({
          from: momentInstance().toDate(),
          to: momentInstance().add(30, 'minutes').toDate(),
        });
        return;
      }

      //* 1 Hours
      if (dateType === DateOptionEnum.oneHour) {
        setDateRanges({
          from: momentInstance().toDate(),
          to: momentInstance().add(1, 'hours').toDate(),
        });
        return;
      }

      //* Today
      if (dateType === DateOptionEnum.today) {
        setDateRanges({
          from: momentInstance().startOf('date').toDate(),
          to: momentInstance().endOf('date').toDate(),
        });
        return;
      }

      //* Yesterday
      if (dateType === DateOptionEnum.yesterday) {
        setDateRanges({
          from: momentInstance().subtract(1, 'days').startOf('date').toDate(),
          to: momentInstance().subtract(1, 'days').endOf('date').toDate(),
        });
        return;
      }

      //* This week
      if (dateType === DateOptionEnum.thisWeek) {
        setDateRanges({
          from: momentInstance().startOf('weeks').toDate(),
          to: momentInstance().endOf('weeks').toDate(),
        });
        return;
      }

      //* Last week
      if (dateType === DateOptionEnum.lastWeek) {
        setDateRanges({
          from: momentInstance().subtract(1, 'weeks').startOf('weeks').toDate(),
          to: momentInstance().subtract(1, 'weeks').endOf('weeks').toDate(),
        });
        return;
      }

      //* Last 7 Days
      if (dateType === DateOptionEnum.last7Days) {
        setDateRanges({
          from: momentInstance().subtract(7, 'days').startOf('date').toDate(),
          to: momentInstance().endOf('date').toDate(),
        });
        return;
      }

      //* Last 30 Days
      if (dateType === DateOptionEnum.last30Days) {
        setDateRanges({
          from: momentInstance().subtract(30, 'days').startOf('date').toDate(),
          to: momentInstance().endOf('date').toDate(),
        });
        return;
      }

      //* This month
      if (dateType === DateOptionEnum.thisMonth) {
        setDateRanges({
          from: momentInstance().startOf('months').toDate(),
          to: momentInstance().endOf('months').toDate(),
        });
        return;
      }

      //* Last month
      if (dateType === DateOptionEnum.lastMonth) {
        setDateRanges({
          from: momentInstance().subtract(1, 'months').startOf('months').toDate(),
          to: momentInstance().subtract(1, 'months').endOf('months').toDate(),
        });
        return;
      }

      //* This Year
      if (dateType === DateOptionEnum.thisYear) {
        setDateRanges({
          from: momentInstance().startOf('years').toDate(),
          to: momentInstance().endOf('years').toDate(),
        });
        return;
      }

      //* Custom range
      setDateRanges({
        from: momentInstance().toDate(),
        to: momentInstance().toDate(),
      });
    },
    [setDateRanges]
  );

  const handleTime = (field) => (e) => {
    const time = e.target.value;

    const date = momentInstance(dateRanges?.[field]);
    const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10));
    date.set('hours', hours);
    date.set('minutes', minutes);
    date.set('seconds', 0);

    setDateRanges({
      ...dateRanges,
      [field]: date.toDate(),
    });
  };

  const handleSelectTime = (field) => (time) => {
    const date = momentInstance(dateRanges[field]);
    const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10));
    date.set('hours', hours);
    date.set('minutes', minutes);
    date.set('seconds', 0);

    setDateRanges({
      ...dateRanges,
      [field]: date.toDate(),
    });
  };

  //! Render
  const renderInputTime = () => {
    if (disableSelectTime) {
      return null;
    }

    if (onlyHours) {
      return (
        <div className='rdp tw-px-3'>
          <div className='tw-flex tw-flex-col sm:tw-flex-row tw-space-y-4 sm:tw-space-x-4 sm:tw-space-y-0'>
            <div className='tw-space-y-4 rdp-caption_start tw-w-full md:tw-w-[50%] tw-text-right'>
              <Select24Hours
                className='tw-ml-1 tw-inline-block tw-min-w-[100px]'
                date={dateRanges?.from}
                onChange={handleSelectTime('from')}
                label='Time:'
                v2
              />
            </div>
            <div className='tw-space-y-4 rdp-caption_start tw-w-full md:tw-w-[50%] tw-text-right'>
              <Select24Hours
                className='tw-ml-1 tw-inline-block tw-min-w-[100px]'
                date={dateRanges?.to}
                onChange={handleSelectTime('to')}
                label='Time:'
                v2
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='rdp tw-px-3'>
        <div className='tw-flex tw-flex-col sm:tw-flex-row tw-space-y-4 sm:tw-space-x-4 sm:tw-space-y-0'>
          <div className='tw-space-y-4 rdp-caption_start tw-w-full md:tw-w-[50%] tw-text-right'>
            <Select24HoursAndMinutes
              date={dateRanges?.from}
              onChange={(date) => {
                setDateRanges({ ...dateRanges, from: date });
              }}
              className='tw-max-w-[150px] tw-ml-auto'
            />
          </div>
          <div className='tw-space-y-4 rdp-caption_start tw-w-full md:tw-w-[50%] tw-text-right'>
            <Select24HoursAndMinutes
              date={dateRanges?.to}
              onChange={(date) => {
                setDateRanges({ ...dateRanges, to: date });
              }}
              className='tw-max-w-[150px] tw-ml-auto'
            />
          </div>
        </div>
      </div>
    );
  };

  const renderFromTo = () => {
    return (
      <Fragment>
        {value?.from ? (
          value?.to ? (
            <>
              <span>{momentInstance(value?.from).format(format)}</span>
              <span>-</span>
              <span>{momentInstance(value?.to).format(format)}</span>
              {disableSelectTime && <span>UTC</span>}
            </>
          ) : (
            momentInstance(value?.from).format(format)
          )
        ) : (
          <span>{placeholder || 'Pick a date range'}</span>
        )}
      </Fragment>
    );
  };

  return (
    <div className={cn('component:DateRange tw-w-full', classNameContainer)}>
      {!v2 && label && (
        <label className={cn('tw-block tw-mb-1', required && 'required')}>{label}</label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {v2 ? (
            <Button
              variant='outline'
              className={cn(
                'tw-w-full tw-border-dashed !tw-min-h-9 tw-h-auto tw-py-[6px] dark:tw-bg-primary-darkInput',
                isInvalid && 'tw-border-red-500 tw-text-red-500'
              )}
            >
              {dateRanges && isClearable ? (
                <div
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickClear && onClickClear();
                  }}
                  className={cn(
                    'tw-rounded-sm tw-opacity-70 tw-transition-opacity tw-hover:opacity-100 tw-focus-visible:outline-none tw-focus-visible:ring-1 tw-focus-visible:ring-ring',
                    isInvalid && 'tw-text-red-500',
                    'tw-flex tw-items-center'
                  )}
                >
                  <XCircle />
                </div>
              ) : (
                <div className='tw-flex tw-items-center'>
                  <CalendarIcon />
                </div>
              )}
              <div className='tw-ml-1'>{label}</div>
              <Separator orientation='vertical' className='tw-mx-0.5 tw-h-4' />
              {renderFromTo()}
            </Button>
          ) : (
            <Button
              variant={'outline'}
              className={cn(
                'tw-justify-start tw-text-left tw-font-normal dark:tw-bg-[var(--bg-dark-input)] tw-flex-wrap md:tw-flex-nowrap tw-h-auto md:tw-h-9 tw-w-full',
                !dateRanges && 'tw-text-muted-foreground',
                isInvalid && 'tw-border-red-500'
              )}
            >
              <CalendarIcon />
              {renderFromTo()}
              {isClearable && dateRanges && (
                <Button
                  variant='ghost'
                  className='tw-p-1 hover:tw-bg-transparent'
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickClear && onClickClear();
                  }}
                >
                  <X />
                </Button>
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className='tw-w-auto tw-p-0 dark:tw-bg-[var(--bg-dark)]' align='start'>
          <div className='tw-flex tw-flex-col md:tw-flex-row tw-max-h-[400px] tw-overflow-auto'>
            <div>
              <Calendar
                showOutsideDays={false}
                initialFocus
                mode='range'
                month={month}
                onMonthChange={setMonth}
                selected={dateRanges}
                onSelect={() => {
                  //* WTF! selected only work if onSelect has value???
                }}
                onDayClick={(date) => {
                  if (!date) return;
                  const storedFromTime = lastTimeRef.current.from;
                  const storedToTime = lastTimeRef.current.to;
                  const prevFromMoment = dateRanges?.from ? momentInstance(dateRanges.from) : null;
                  const prevFromTime = getTimePayload(prevFromMoment);
                  //* If from / to is selected -> user click -> this will clear to date and start selecting from date
                  if (!dateRanges?.from || (dateRanges?.from && dateRanges?.to)) {
                    const nextFrom = momentInstance(date);
                    const defaultStartOfDay = momentInstance(date).startOf('date');
                    applyTimeToMoment(nextFrom, prevFromTime || storedFromTime);
                    if (!prevFromTime && !storedFromTime) {
                      nextFrom.set({
                        hour: defaultStartOfDay.hour(),
                        minute: defaultStartOfDay.minute(),
                        second: defaultStartOfDay.second(),
                      });
                    }
                    const nextFromDate = nextFrom.toDate();
                    lastTimeRef.current.from = getTimePayload(nextFrom);
                    setDateRanges({ from: nextFromDate, to: undefined });
                  } else if (dateRanges?.from && !dateRanges?.to) {
                    //* if from selected -> select to
                    if (date >= dateRanges?.from) {
                      const nextTo = momentInstance(date);
                      const prevToMoment = dateRanges?.to ? momentInstance(dateRanges.to) : null;
                      const prevToTime = getTimePayload(prevToMoment);
                      const defaultEndOfDay = momentInstance(date).endOf('date');
                      const defaultStartOfDay = momentInstance(date).startOf('date');
                      const fallbackFromTime = prevFromTime || storedFromTime;

                      if (prevToTime) {
                        applyTimeToMoment(nextTo, prevToTime);
                      } else if (storedToTime) {
                        applyTimeToMoment(nextTo, storedToTime);
                      } else if (fallbackFromTime) {
                        applyTimeToMoment(nextTo, fallbackFromTime);
                        const isStartOfDayFallback =
                          fallbackFromTime.hours === defaultStartOfDay.hour() &&
                          fallbackFromTime.minutes === defaultStartOfDay.minute() &&
                          fallbackFromTime.seconds === defaultStartOfDay.second();
                        if (isStartOfDayFallback) {
                          nextTo.set({
                            hour: defaultEndOfDay.hour(),
                            minute: defaultEndOfDay.minute(),
                            second: defaultEndOfDay.second(),
                          });
                        }
                      } else {
                        nextTo.set({
                          hour: defaultEndOfDay.hour(),
                          minute: defaultEndOfDay.minute(),
                          second: defaultEndOfDay.second(),
                        });
                      }
                      const nextToDate = nextTo.toDate();
                      lastTimeRef.current.to = getTimePayload(nextTo);
                      setDateRanges({ ...dateRanges, to: nextToDate });
                    } else {
                      // User clicked an earlier date; set as new from, preserving old from's time
                      const nextFrom = momentInstance(date);
                      const defaultStartOfDay = momentInstance(date).startOf('date');
                      applyTimeToMoment(nextFrom, prevFromTime || storedFromTime);
                      if (!prevFromTime && !storedFromTime) {
                        nextFrom.set({
                          hour: defaultStartOfDay.hour(),
                          minute: defaultStartOfDay.minute(),
                          second: defaultStartOfDay.second(),
                        });
                      }
                      const nextFromDate = nextFrom.toDate();
                      lastTimeRef.current.from = getTimePayload(nextFrom);
                      setDateRanges({ from: nextFromDate, to: dateRanges?.from });
                    }
                  }
                }}
                numberOfMonths={2}
                captionLayout='dropdown'
              />
              {renderInputTime()}
            </div>

            <div className='date-range-picker-presets tw-flex tw-flex-col tw-gap-2 tw-pr-2 tw-pt-2 tw-min-w-[150px] tw-max-h-[300px] tw-overflow-auto tw-min-h-[200px] md:tw-min-h-0'>
              {optionsDate.map((el) => {
                return (
                  <Button
                    key={el.label}
                    className='tw-text-right tw-justify-end'
                    variant='ghost'
                    onClick={() => {
                      setQuickSelectDate(el);
                      switchDateByQuickDate(el.value);
                    }}
                  >
                    {quickSelectDate.value === el.value && <Check />} {el.label}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className='tw-mt-3 tw-flex tw-p-2 tw-justify-end tw-gap-1'>
            <Button type='button' variant='ghost' onClick={() => setOpen(false)}>
              Close
            </Button>
            <NewDesign.Button
              type='button'
              onClick={() => {
                setOpen(false);
                const dateRangesForSubmit = cloneDeep(dateRanges);

                if (!dateRanges?.to) {
                  dateRangesForSubmit.to = momentInstance(dateRangesForSubmit.from).toDate();
                }

                //* If user only select date range with only hours -> set minutes and second to zero
                //* E.g: 01/01/2024 13:54 -> 01/02/2024 23:59 will be 01/01/2024 13:00 -> 01/02/2024 23:00
                if (onlyHours) {
                  dateRangesForSubmit.from = momentInstance(dateRangesForSubmit.from)
                    .set({ minute: 0, second: 0 })
                    .toDate();
                  dateRangesForSubmit.to = momentInstance(dateRangesForSubmit.to)
                    .set({ minute: 0, second: 0 })
                    .toDate();
                }

                //* In case user select only date without time, the from should be startOf Date and to should be endOf Date
                //* E.g: 01/01/2024 00:00 -> 01/02/2024 23:59
                if (disableSelectTime) {
                  dateRangesForSubmit.from = momentInstance(dateRangesForSubmit.from)
                    .startOf('date')
                    .toDate();
                  dateRangesForSubmit.to = momentInstance(dateRangesForSubmit.to)
                    .endOf('date')
                    .toDate();
                }

                onSubmit && onSubmit(dateRangesForSubmit);
              }}
            >
              Apply
            </NewDesign.Button>
          </div>
        </PopoverContent>
      </Popover>

      {isInvalid && <p className='tw-text-xs tw-mt-1 tw-text-red-500'>{msgError}</p>}
    </div>
  );
};

DateRange.propTypes = propTypes;
export default DateRange;
